import { get } from 'svelte/store';
import { v4 as uuidv4 } from 'uuid';
import { ConfigStore } from '../../store/config';
import type { IRequestSearch } from '../../interfaces/request-search-question.interface';
import type { IConversationEvent } from '../../interfaces/conversation-event.interface';
import { SessionStoreHandler } from '../../helpers/session-storage-handler';
import { UserStateHelper } from '../../helpers/user-state-helper';
import { trackedFetch } from '../../telemetry/tracked-fetch';
import { defaultLogger as logger } from '../../telemetry/logger';
import type { IOAuthAuthorizationRequest } from '../../interfaces/oauth-authorization.interface';

type ConversationEventHandler = (
  event: IConversationEvent
) => void | Promise<void>;

export type ConversationHttpError = Error & {
  status?: number;
  statusText?: string;
  response?: Response;
  body?: {
    status?: number;
    error?: string;
    message?: string;
    latest_question_id?: string;
  };
  errorType?: string;
  serverMessage?: string;
  latestQuestionId?: string;
};

class ConversationConnection {
  private eventSource?: EventSource;
  private threadId?: string;
  private readyPromise?: Promise<void>;
  private readyResolve?: () => void;
  private readyReject?: (reason?: any) => void;
  private connected = false;
  private idleTimer?: ReturnType<typeof setTimeout>;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private hasOpened = false;
  private readonly handlers = new Set<ConversationEventHandler>();

  public getClientId(): string {
    let clientId = SessionStoreHandler.get()?.clientId;
    if (!clientId) {
      clientId = uuidv4();
      SessionStoreHandler.update({ clientId });
    }
    return clientId;
  }

  public createClientMessageId(): string {
    return uuidv4();
  }

  public subscribe(handler: ConversationEventHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  public isConnected(threadId: string): boolean {
    return (
      this.threadId === threadId &&
      this.connected &&
      this.eventSource?.readyState === EventSource.OPEN
    );
  }

  public async ensureConnected(threadId: string): Promise<void> {
    if (
      this.threadId === threadId &&
      this.eventSource?.readyState === EventSource.OPEN
    ) {
      this.touch();
      return;
    }
    if (this.threadId === threadId && this.readyPromise) {
      return this.readyPromise;
    }

    this.close();
    this.threadId = threadId;
    const url = new URL(
      `${get(ConfigStore).apiBaseUrl}/1/conversation/${encodeURIComponent(threadId)}/events`
    );
    url.searchParams.set('clientId', this.getClientId());
    const tenantId = get(ConfigStore)?.serverConfig?.tenant_id;
    if (tenantId) url.searchParams.set('tenant', tenantId);

    this.resetReadyPromise();

    const timeout = setTimeout(() => {
      const error = new Error('Persistent conversation connection timed out');
      error.name = 'PersistentUnavailableError';
      this.readyReject?.(error);
      this.close();
    }, 5000);

    const source = new EventSource(url.href);
    this.eventSource = source;

    source.addEventListener('ready', (raw) => {
      clearTimeout(timeout);
      clearTimeout(this.reconnectTimer);
      this.connected = true;
      this.touch();
      this.readyResolve?.();
      this.dispatch(raw as MessageEvent, this.hasOpened);
      this.hasOpened = true;
    });
    [
      'turn.started',
      'answer.chunk',
      'turn.completed',
      'turn.error',
      'authorization.required',
      'authorization.succeeded',
      'authorization.failed',
      'authorization.cancelled',
    ].forEach((eventName) => {
      source.addEventListener(eventName, (raw) => {
        this.touch();
        this.dispatch(raw as MessageEvent);
      });
    });
    source.addEventListener('connection.closing', (raw) => {
      this.dispatch(raw as MessageEvent);
      this.close();
    });
    source.onerror = () => {
      if (source.readyState === EventSource.CLOSED) {
        clearTimeout(timeout);
        const error = new Error('Persistent conversation connection closed');
        error.name = 'PersistentUnavailableError';
        this.readyReject?.(error);
        this.close();
      } else if (this.connected) {
        this.connected = false;
        this.resetReadyPromise();
        this.reconnectTimer = setTimeout(() => {
          const error = new Error(
            'Persistent conversation reconnection timed out'
          );
          error.name = 'PersistentUnavailableError';
          this.readyReject?.(error);
          this.close();
        }, 5000);
      }
    };

    return this.readyPromise;
  }

  public async sendQuestion(
    threadId: string,
    params: IRequestSearch,
    clientMessageId: string,
    signal?: AbortSignal
  ): Promise<{ request_id: string; thread_id: string }> {
    await this.ensureConnected(threadId);
    this.touch();

    const request = this.prepareRequest(params, clientMessageId);
    const url = this.questionUrl(
      `/1/conversation/${encodeURIComponent(threadId)}/question`,
      params
    );
    const tenantId = get(ConfigStore)?.serverConfig?.tenant_id ?? '';
    const response = await trackedFetch(tenantId, url.href, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal,
        body: JSON.stringify(request),
      }).catch(async (error) => {
        throw await this.enrichHttpError(error);
      });
    this.touch();
    return response.json();
  }

  public async cancelQuestion(
    threadId: string,
    cancellation: { requestId?: string; clientMessageId?: string }
  ): Promise<void> {
    const url = new URL(
      `${get(ConfigStore).apiBaseUrl}/1/conversation/${encodeURIComponent(threadId)}/cancel`
    );
    const tenantId = get(ConfigStore)?.serverConfig?.tenant_id;
    if (tenantId) url.searchParams.set('tenant', tenantId);

    await trackedFetch(tenantId ?? '', url.href, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: this.getClientId(),
          client_message_id: cancellation.clientMessageId,
          request_id: cancellation.requestId,
        }),
      }).catch(async (error) => {
        throw await this.enrichHttpError(error);
      });
  }

  public async bootstrap(
    params: IRequestSearch,
    clientMessageId: string,
    signal?: AbortSignal
  ): Promise<Response> {
    const request = this.prepareRequest(params, clientMessageId);
    const url = this.questionUrl('/1/conversation/question', params);
    const tenantId = get(ConfigStore)?.serverConfig?.tenant_id ?? '';
    const response = await trackedFetch(tenantId, url.href, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal,
        body: JSON.stringify(request),
      }).catch(async (error) => {
        throw await this.enrichHttpError(error);
      });
    this.touch();
    return response;
  }

  public async getPendingAuthorizations(
    threadId: string
  ): Promise<IOAuthAuthorizationRequest[]> {
    const url = new URL(
      `${get(ConfigStore).apiBaseUrl}/1/oauth/thread/${encodeURIComponent(threadId)}/pending`
    );
    const tenantId = get(ConfigStore)?.serverConfig?.tenant_id;
    if (tenantId) url.searchParams.set('tenant', tenantId);
    const response = await trackedFetch(tenantId ?? '', url.href, {
      method: 'GET',
    }).catch(async (error) => {
      throw await this.enrichHttpError(error);
    });
    return response.ok ? response.json() : [];
  }

  public close(): void {
    clearTimeout(this.idleTimer);
    clearTimeout(this.reconnectTimer);
    this.eventSource?.close();
    this.eventSource = undefined;
    this.threadId = undefined;
    this.readyPromise = undefined;
    this.readyResolve = undefined;
    this.readyReject = undefined;
    this.connected = false;
  }

  private prepareRequest(
    params: IRequestSearch,
    clientMessageId: string
  ): IRequestSearch & { client_id: string; client_message_id: string } {
    const request: IRequestSearch & {
      client_id: string;
      client_message_id: string;
    } = {
      ...params,
      client_id: this.getClientId(),
      client_message_id: clientMessageId,
    };
    delete request.requestType;
    if (get(ConfigStore)?.serverConfig?.id) {
      request.config = get(ConfigStore)?.serverConfig?.id;
    }
    UserStateHelper.setRequestProperties(request);
    return request;
  }

  private questionUrl(path: string, params: IRequestSearch): URL {
    const url = new URL(`${get(ConfigStore).apiBaseUrl}${path}`);
    const tenantId = get(ConfigStore)?.serverConfig?.tenant_id;
    if (tenantId) url.searchParams.set('tenant', tenantId);
    if (
      params.requestType &&
      params.requestType !== 'greeting' &&
      get(ConfigStore)?.cacheMsg?.includes(params.requestType)
    ) {
      url.searchParams.set('cache', 'true');
    }
    url.searchParams.set('stream', 'true');
    return url;
  }

  private dispatch(raw: MessageEvent, reconnected: boolean = false): void {
    try {
      const event = JSON.parse(raw.data) as IConversationEvent;
      event.reconnected = reconnected;
      this.handlers.forEach((handler) => {
        Promise.resolve(handler(event)).catch((error) =>
          logger.error('Persistent conversation event handler failed', error)
        );
      });
    } catch (error) {
      logger.error('Failed to parse persistent conversation event', error);
    }
  }

  private resetReadyPromise(): void {
    this.readyPromise = new Promise<void>((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });
  }

  private async enrichHttpError(
    error: unknown
  ): Promise<ConversationHttpError> {
    const httpError = error as ConversationHttpError;
    const response = httpError?.response;
    if (!response) return httpError;

    await response
      .clone()
      .json()
      .then((body) => {
        httpError.body = body;
        httpError.errorType = body?.error;
        httpError.serverMessage = body?.message;
        httpError.latestQuestionId = body?.latest_question_id;
        if (body?.message) httpError.message = body.message;
      })
      .catch(() => undefined);
    return httpError;
  }

  private touch(): void {
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(
      () => this.close(),
      (get(ConfigStore).conversationIdleTimeout ?? 300) * 1000
    );
  }
}

export const conversationConnection = new ConversationConnection();
